import { useState, useEffect, useContext, useRef } from "react"
import ReactDOM from "react-dom"
import { AlertsContext } from "../../../../../utils/providers/alerts"
import { useReadOnly } from "../../../../../utils/providers/read-only"
import { db } from "../../../../../utils/firebase"
import { PencilIcon, TrashIcon } from "../../../../../utils/svgs-v2"
import "./group-name.scss"

// UI components
import Checkbox from "../../../../../components/ui/checkbox/checkbox"
import Button from "../../../../../components/ui/button/button"
import Input from "../../../../../components/ui/inputs/input"

// Trim and title-case names (handles hyphenated and multi-word names)
function formatName(raw) {
	if (raw === null || raw === undefined) return ""

	const cleaned = String(raw).replace(/\s+/g, " ").trim().toLowerCase()
	if (!cleaned) return ""

	return cleaned.replace(/(^|[\s\-'])\p{L}/gu, (match) => match.toUpperCase())
}

// Trim, remove internal whitespace, lowercase the whole email
function formatEmail(raw) {
	if (raw === null || raw === undefined) return ""
	return String(raw).replace(/\s+/g, "").toLowerCase()
}

// Normalise phone numbers to an international (+country) format, defaulting
// UK-style "0" prefixes to +44 since most of our client base is British.
function formatPhone(raw) {
	if (raw === null || raw === undefined) return ""

	const cleaned = String(raw).replace(/[\s\-().]/g, "")
	if (!cleaned) return ""

	if (cleaned.startsWith("+")) return "+" + cleaned.slice(1).replace(/\D/g, "")
	if (cleaned.startsWith("00")) return "+" + cleaned.slice(2).replace(/\D/g, "")

	const digits = cleaned.replace(/\D/g, "")
	if (!digits) return ""

	if (digits.startsWith("44")) return "+" + digits
	if (digits.startsWith("0")) return "+44" + digits.slice(1)

	return digits
}

const MASTERS_DAY_LABELS = {
	monday: "Monday",
	tuesday: "Tuesday",
	wednesday: "Wednesday",
	thursday: "Thursday",
	friday: "Friday",
	saturday: "Saturday",
	sunday: "Sunday",
}

// Ordered Monday -> Sunday so the summary string reads in week order
const MASTERS_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]

const EMPTY_MASTERS_DAYS = MASTERS_DAYS.reduce((acc, day) => {
	acc[day] = false
	return acc
}, {})

function summariseMastersDays(mastersDays = {}) {
	const days = MASTERS_DAYS.filter((day) => mastersDays?.[day])
	if (days.length === 0) return "-"
	return days.map((day) => MASTERS_DAY_LABELS[day]).join(", ")
}

// Returns a row for the group name table + an edit modal for fuller details
export default function GroupName({ bookingID, groupNameID, clientID, autoOpen = false, onAutoOpenHandled }) {
	const readOnly = useReadOnly()
	const [saving, setSaving] = useState(false)
	const [deleting, setDeleting] = useState(false)
	const [mounted, setMounted] = useState(false)
	const [loadingLead, setLoadingLead] = useState(false)

	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")
	const [phone, setPhone] = useState("")
	const [mastersDays, setMastersDays] = useState(EMPTY_MASTERS_DAYS)

	// Draft state used only while the edit modal is open
	const [modalOpen, setModalOpen] = useState(false)
	const [draftFirstName, setDraftFirstName] = useState("")
	const [draftLastName, setDraftLastName] = useState("")
	const [draftEmail, setDraftEmail] = useState("")
	const [draftPhone, setDraftPhone] = useState("")
	const [draftMastersDays, setDraftMastersDays] = useState(EMPTY_MASTERS_DAYS)

	const autoOpenHandledRef = useRef(false)

	const { pushAlert } = useContext(AlertsContext)

	useEffect(() => {
		const unsubscribe = db.doc(`bookings/${bookingID}/group_names/${groupNameID}`).onSnapshot((nameDoc) => {
			if (!nameDoc.exists) return

			const data = nameDoc.data()
			setFirstName(data.first_name || "")
			setLastName(data.last_name || "")
			setEmail(data.email || "")
			setPhone(data.phone || "")
			setMastersDays({ ...EMPTY_MASTERS_DAYS, ...(data.masters_days || {}) })
			setMounted(true)
		})

		return () => unsubscribe()
	}, [bookingID, groupNameID])

	// Auto-open the edit modal once for freshly-added rows
	useEffect(() => {
		if (!autoOpen || !mounted || autoOpenHandledRef.current) return

		autoOpenHandledRef.current = true
		setDraftFirstName(firstName)
		setDraftLastName(lastName)
		setDraftEmail(email)
		setDraftPhone(phone)
		setDraftMastersDays({ ...EMPTY_MASTERS_DAYS, ...mastersDays })
		setModalOpen(true)

		if (typeof onAutoOpenHandled === "function") onAutoOpenHandled()
	}, [autoOpen, mounted, firstName, lastName, email, phone, mastersDays, onAutoOpenHandled])

	const openEditModal = () => {
		setDraftFirstName(firstName)
		setDraftLastName(lastName)
		setDraftEmail(email)
		setDraftPhone(phone)
		setDraftMastersDays({ ...EMPTY_MASTERS_DAYS, ...mastersDays })
		setModalOpen(true)
	}

	const closeModal = () => {
		if (saving || deleting) return
		setModalOpen(false)
	}

	// Populate the draft fields from the booking's lead client record
	const useLeadBookerDetails = async () => {
		if (!clientID || loadingLead) return

		setLoadingLead(true)

		try {
			const clientDoc = await db.doc(`clients/${clientID}`).get()

			if (!clientDoc.exists) {
				pushAlert({
					type: "ERROR",
					title: "Lead client not found",
					body: "Could not find the lead booker's profile for this booking.",
				})
				return
			}

			const data = clientDoc.data() || {}
			setDraftFirstName(formatName(data.first_name))
			setDraftLastName(formatName(data.last_name))
			setDraftEmail(formatEmail(data.email))
			setDraftPhone(formatPhone(data.phone))
		} catch (err) {
			console.log(err)
			pushAlert({
				type: "ERROR",
				title: "Could not load lead booker",
				body: err.message,
			})
		} finally {
			setLoadingLead(false)
		}
	}

	const saveGroupName = async () => {
		setSaving(true)

		try {
			await db.doc(`bookings/${bookingID}/group_names/${groupNameID}`).set(
				{
					first_name: draftFirstName,
					last_name: draftLastName,
					email: draftEmail,
					phone: draftPhone,
					masters_days: draftMastersDays,
				},
				{ merge: true },
			)

			pushAlert({
				type: "SUCCESS",
				title: "Group name saved",
			})
			setModalOpen(false)
		} catch (err) {
			console.log(err)
			pushAlert({
				type: "ERROR",
				title: "Error saving this name",
				body: err.message,
			})
		} finally {
			setSaving(false)
		}
	}

	const deleteGroupName = async () => {
		setDeleting(true)

		try {
			await db.doc(`bookings/${bookingID}/group_names/${groupNameID}`).delete()
			setModalOpen(false)
		} catch (err) {
			console.log(err)
			pushAlert({
				type: "ERROR",
				title: "Error deleting this name",
				body: err.message,
			})
		} finally {
			setDeleting(false)
		}
	}

	const deleteFromRow = () => {
		if (deleting) return

		const displayName = [firstName, lastName].filter(Boolean).join(" ").trim()
		const confirmMessage = displayName ? `Remove ${displayName} from this booking?` : "Remove this person from the booking?"

		if (!window.confirm(confirmMessage)) return

		deleteGroupName()
	}

	if (!mounted) return null

	return (
		<>
			<tr>
				<td>{firstName || <small className="no-value">-</small>}</td>
				<td>{lastName || <small className="no-value">-</small>}</td>
				<td>{email || <small className="no-value">-</small>}</td>
				<td>{phone || <small className="no-value">-</small>}</td>
				<td>{summariseMastersDays(mastersDays)}</td>
				<td style={{ textAlign: "right" }}>
					{!readOnly && (
						<div className="group-name-row-actions">
							<button
								type="button"
								className="group-name-icon-button"
								onClick={openEditModal}
								aria-label="Edit person">
								<PencilIcon />
							</button>
							<button
								type="button"
								className="group-name-icon-button"
								onClick={deleteFromRow}
								disabled={deleting}
								aria-label="Remove person">
								<TrashIcon />
							</button>
						</div>
					)}
				</td>
			</tr>

			{modalOpen &&
				ReactDOM.createPortal(
					<div
						className="group-name-modal-backdrop"
						onClick={closeModal}>
						<div
							className="group-name-modal"
							onClick={(event) => event.stopPropagation()}>
							<div className="group-name-modal-header">
								<h3>Edit person</h3>
								{clientID && (
									<button
										type="button"
										className="group-name-modal-use-lead"
										onClick={useLeadBookerDetails}
										disabled={loadingLead}>
										{loadingLead ? "Loading..." : "Use lead booker details"}
									</button>
								)}
							</div>

							<div className="group-name-modal-body">
								<div className="group-name-modal-grid">
									<label>
										<span>First name</span>
										<Input
											value={draftFirstName}
											onChange={(value) => setDraftFirstName(value)}
											placeholder="First name"
										/>
									</label>
									<label>
										<span>Last name</span>
										<Input
											value={draftLastName}
											onChange={(value) => setDraftLastName(value)}
											placeholder="Last name"
										/>
									</label>
									<label className="group-name-modal-full">
										<span>Email</span>
										<Input
											value={draftEmail}
											onChange={(value) => setDraftEmail(value)}
											placeholder="Email"
										/>
									</label>
									<label className="group-name-modal-full">
										<span>Phone</span>
										<Input
											value={draftPhone}
											onChange={(value) => setDraftPhone(value)}
											placeholder="Phone"
										/>
									</label>
								</div>

								<div className="group-name-modal-days">
									<span className="group-name-modal-days-title">Masters viewing days</span>
									<div className="group-name-modal-days-grid">
										{MASTERS_DAYS.map((day) => (
											<Checkbox
												key={day}
												label={MASTERS_DAY_LABELS[day]}
												checked={!!draftMastersDays[day]}
												onClick={() =>
													setDraftMastersDays({
														...draftMastersDays,
														[day]: !draftMastersDays[day],
													})
												}
											/>
										))}
									</div>
								</div>
							</div>

							<div className="group-name-modal-footer">
								<button
									type="button"
									className="group-name-modal-delete"
									onClick={deleteGroupName}
									disabled={deleting || saving}
									aria-label="Remove person">
									<TrashIcon />
									<span>{deleting ? "Removing..." : "Remove"}</span>
								</button>

								<div className="group-name-modal-footer-right">
									<Button
										type="OUTLINE"
										label="Cancel"
										small={true}
										onClick={closeModal}
									/>
									<Button
										label="Save"
										small={true}
										loading={saving}
										loadingText="Saving..."
										onClick={saveGroupName}
									/>
								</div>
							</div>
						</div>
					</div>,
					document.body,
				)}
		</>
	)
}
